import { useState, useEffect, useCallback, useRef } from 'react'
import type { ApiClient } from '../api/client'
import type { PendingVersionSummary } from '../api/types'

interface UsePendingChangesResult {
  pendingBySampleId: Record<string, PendingVersionSummary>
  loading: boolean
  error: string | null
  refetch: () => void
}

export function usePendingChanges(
  apiClient: ApiClient | null,
  sampleIds: string[]
): UsePendingChangesResult {
  const [pendingBySampleId, setPendingBySampleId] = useState<Record<string, PendingVersionSummary>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track the last fetched sample IDs to avoid redundant fetches
  const lastFetchedRef = useRef<string>('')

  const fetchPendingChanges = useCallback(async () => {
    if (!apiClient || sampleIds.length === 0) {
      setPendingBySampleId({})
      return
    }

    // Create a stable key from sample IDs
    const sampleKey = sampleIds.slice().sort().join(',')
    if (sampleKey === lastFetchedRef.current) {
      // Already fetched for these samples
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.checkPendingSamples(sampleIds)
      setPendingBySampleId(response.pendingBySampleId)
      lastFetchedRef.current = sampleKey
    } catch (err) {
      console.error('Failed to fetch pending changes:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pending changes')
    } finally {
      setLoading(false)
    }
  }, [apiClient, sampleIds])

  useEffect(() => {
    fetchPendingChanges()
  }, [fetchPendingChanges])

  const refetch = useCallback(() => {
    lastFetchedRef.current = '' // Clear cache to force refetch
    fetchPendingChanges()
  }, [fetchPendingChanges])

  // Listen for pendingChangesUpdated event to refetch when changes are accepted/rejected
  useEffect(() => {
    const handlePendingChangesUpdated = () => {
      lastFetchedRef.current = '' // Clear cache to force refetch
      fetchPendingChanges()
    }

    window.addEventListener('pendingChangesUpdated', handlePendingChangesUpdated)
    return () => {
      window.removeEventListener('pendingChangesUpdated', handlePendingChangesUpdated)
    }
  }, [fetchPendingChanges])

  return {
    pendingBySampleId,
    loading,
    error,
    refetch,
  }
}
